import { API_ROUTES } from '../../../lib/apiRoutes';
import { apiRequest } from '../../../lib/api';
import { buildIdempotencyKey, buildStickyIdempotencyKey } from '../../../lib/idempotency';
import type {
  CreateSupportTicketRequest,
  SupportAdminListQuery,
  SupportFaqAdminEntry,
  SupportFaqAdminListResponse,
  SupportFaqEntry,
  SupportFaqEntryResponse,
  SupportFaqPatchRequest,
  SupportFaqPublishRequestResponse,
  SupportFaqPublishRequestResult,
  SupportFaqUpsertRequest,
  SupportTicket,
  SupportTicketResponse,
  SupportTicketsListResponse,
  SupportTicketStatus
} from '../types/support.types';

export type {
  CreateSupportTicketRequest,
  SupportAdminListQuery,
  SupportFaqAdminEntry,
  SupportFaqAudience,
  SupportFaqEntry,
  SupportFaqPatchRequest,
  SupportFaqPublishRequestResult,
  SupportFaqUpsertRequest,
  SupportMessage,
  SupportTicket,
  SupportTicketPriority,
  SupportTicketResponse,
  SupportTicketsListResponse,
  SupportTicketStatus
} from '../types/support.types';

export const supportApi = {
  async listMine(): Promise<SupportTicket[]> {
    const response = await apiRequest<SupportTicketsListResponse>(`${API_ROUTES.v1.support}/me`);
    return response.data || [];
  },
  async create(payload: CreateSupportTicketRequest): Promise<SupportTicket> {
    const response = await apiRequest<SupportTicketResponse>(API_ROUTES.v1.support, {
      method: 'POST',
      body: JSON.stringify(payload),
      idempotencyKey: buildStickyIdempotencyKey('supportCreate', [
        payload.subject || 'ticket',
        payload.body || ''
      ]),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('Support ticket was not created. Try again.');
    }
    return response.data;
  },
  async replyAsCustomer(ticketId: string, body: string): Promise<SupportTicket> {
    const response = await apiRequest<SupportTicketResponse>(`${API_ROUTES.v1.support}/${encodeURIComponent(ticketId)}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body }),
      idempotencyKey: buildStickyIdempotencyKey('supportReply', [ticketId, body]),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('Reply was not saved. Try again.');
    }
    return response.data;
  },
  async adminList(query?: SupportAdminListQuery): Promise<SupportTicket[]> {
    const response = await apiRequest<SupportTicketsListResponse>(`${API_ROUTES.v1.support}/admin`, {
      query: query ? { ...query } : undefined
    });
    return response.data || [];
  },
  async adminReply(ticketId: string, body: string, setStatus?: SupportTicketStatus): Promise<SupportTicket> {
    const response = await apiRequest<SupportTicketResponse>(`${API_ROUTES.v1.support}/admin/${encodeURIComponent(ticketId)}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body, setStatus }),
      idempotencyKey: buildStickyIdempotencyKey('supportAdminReply', [ticketId, setStatus || 'default', body]),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('Reply was not saved. Try again.');
    }
    return response.data;
  },
  async adminAssign(ticketId: string, assignedTo: string | null): Promise<SupportTicket> {
    const response = await apiRequest<SupportTicketResponse>(`${API_ROUTES.v1.support}/admin/${encodeURIComponent(ticketId)}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assignedTo }),
      idempotencyKey: buildStickyIdempotencyKey('supportAdminAssign', [ticketId, assignedTo || 'unassigned']),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('Assignment was not saved. Try again.');
    }
    return response.data;
  },
  async adminClose(ticketId: string, note?: string): Promise<SupportTicket> {
    const response = await apiRequest<SupportTicketResponse>(`${API_ROUTES.v1.support}/admin/${encodeURIComponent(ticketId)}/close`, {
      method: 'POST',
      body: JSON.stringify(note ? { note } : {}),
      idempotencyKey: buildStickyIdempotencyKey('supportAdminClose', [ticketId, note || '']),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('Ticket close was not saved. Try again.');
    }
    return response.data;
  },
  async adminListFaqs(): Promise<SupportFaqAdminEntry[]> {
    const response = await apiRequest<SupportFaqAdminListResponse>(`${API_ROUTES.v1.support}/admin/faq`);
    return response.data || [];
  },
  async adminCreateFaq(payload: SupportFaqUpsertRequest): Promise<SupportFaqEntry> {
    const response = await apiRequest<SupportFaqEntryResponse>(`${API_ROUTES.v1.support}/admin/faq`, {
      method: 'POST',
      body: JSON.stringify(payload),
      idempotencyKey: buildIdempotencyKey('supportFaqCreate', [payload.audience]),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('FAQ draft was not saved. Try again.');
    }
    return response.data;
  },
  async adminUpdateFaq(id: string, payload: SupportFaqPatchRequest): Promise<SupportFaqEntry> {
    const response = await apiRequest<SupportFaqEntryResponse>(`${API_ROUTES.v1.support}/admin/faq/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      idempotencyKey: buildIdempotencyKey('supportFaqPatch', [id]),
      requireIdempotencyKey: true
    });
    if (!response?.data?._id) {
      throw new Error('FAQ changes were not saved. Try again.');
    }
    return response.data;
  },
  async adminRequestFaqPublish(id: string): Promise<SupportFaqPublishRequestResult> {
    const response = await apiRequest<SupportFaqPublishRequestResponse>(
      `${API_ROUTES.v1.support}/admin/faq/${encodeURIComponent(id)}/request-publish`,
      {
        method: 'POST',
        body: JSON.stringify({}),
        idempotencyKey: buildIdempotencyKey('supportFaqPublish', [id]),
        requireIdempotencyKey: true
      }
    );
    if (!response?.data?.approvalId) {
      throw new Error('Publish request was not saved. Try again.');
    }
    return response.data;
  },
  async adminUnpublishFaq(id: string): Promise<SupportFaqEntry> {
    const response = await apiRequest<SupportFaqEntryResponse>(
      `${API_ROUTES.v1.support}/admin/faq/${encodeURIComponent(id)}/unpublish`,
      {
        method: 'POST',
        body: JSON.stringify({}),
        idempotencyKey: buildIdempotencyKey('supportFaqUnpublish', [id]),
        requireIdempotencyKey: true
      }
    );
    if (!response?.data?._id) {
      throw new Error('FAQ was not unpublished. Try again.');
    }
    return response.data;
  }
};
